import React, { useEffect, useState } from "react";
import { ref, get } from "firebase/database";
import { database as realtimeDb } from "../FirebaseConfig";
import { useLocation, useNavigate } from "react-router-dom";

export default function Blog() {
  const [blogs, setBlogs] = useState([]);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();


  useEffect(() => {
    window.scrollTo(0, 0);

    fetchBlogs();
  }, []);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const blogId = queryParams.get("id");

    if (blogId) {
      fetchBlogDetails(blogId);
    }
  }, [location.search]);

  // **🔹 Fetch All Blogs**
  const fetchBlogs = async () => {
    try {
      const blogsRef = ref(realtimeDb, "blogs");
      const snapshot = await get(blogsRef);

      if (snapshot.exists()) {
        const data = snapshot.val();
        const blogsArray = Object.entries(data).map(([id, blog]) => ({ id, ...blog }));
        blogsArray.sort((a, b) => new Date(b.date) - new Date(a.date)); // Sort by date (newest first)
        setBlogs(blogsArray);
      }
    } catch (error) {
      console.error("Error fetching blogs:", error);
    }
    setLoading(false);
  };

  // **🔹 Fetch Blog Details by ID**
  const fetchBlogDetails = async (blogId) => {
    try {
      const blogRef = ref(realtimeDb, `blogs/${blogId}`);
      const snapshot = await get(blogRef);

      if (snapshot.exists()) {
        setSelectedBlog({ id: blogId, ...snapshot.val() });
      } else {
        navigate("/blog");
      }
    } catch (error) {
      console.error("Error fetching blog:", error);
      navigate("/blog");
    }
  };

  // **🔹 Get Next & Previous Blog IDs**
  const getNextPrevBlogs = () => {
    if (!selectedBlog) return { next: null, prev: null };

    const index = blogs.findIndex(blog => blog.id === selectedBlog.id);
    return {
      prev: index > 0 ? blogs[index - 1].id : null,
      next: index < blogs.length - 1 ? blogs[index + 1].id : null
    };
  };

  const { next, prev } = getNextPrevBlogs();

  if (loading) {
    return <div className="h-screen flex items-center justify-center text-gray-600">Loading blogs...</div>;
  }

  // **🔹 Show Blog List**
  if (!selectedBlog) {
    return (
      <div className="max-w-7xl mt-27 mx-auto mt-12 p-6">
        <h1 className="text-4xl font-extrabold text-gray-900 text-center">Our Blog</h1>
        <p className="text-lg text-gray-600 text-center mt-2">
          Discover insights, trends, and tips on beekeeping & nature! 🍯🐝
        </p>
        <div className="mt-8 grid gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {blogs.map((blog) => (
            <div
              key={blog.id}
              className="bg-white rounded-lg shadow-lg overflow-hidden cursor-pointer transform hover:scale-105 transition-all duration-300"
              onClick={() => navigate(`/blog?id=${blog.id}`)}
            >
              <img src={blog.thumbnail} alt={blog.title} className="w-full h-48 object-cover" />
              <div className="p-4">
                <h2 className="text-xl font-semibold text-gray-800">{blog.title}</h2>
                <p className="text-gray-500 text-sm mt-1">{blog.date}</p>
                <p className="text-gray-600 mt-2 text-sm">{blog.summary}...</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // **🔹 Show Single Blog**
  return (
    <div className="max-w-5xl mt-27 mx-auto mt-12 p-6">
      <div className="bg-white shadow-lg rounded-lg p-8">
        <h1 className="text-4xl font-bold text-[#f47d02]">{selectedBlog.title}</h1>
        <p className="text-gray-500 text-lg mt-2">{selectedBlog.date}</p>

        {/* Image Gallery */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {selectedBlog.images?.map((img, index) => (
            <div key={index} className="overflow-hidden rounded-lg shadow-md">
              <img src={img} alt={`Blog Image ${index + 1}`} className="w-full h-56 object-cover hover:scale-110 transition-transform duration-300" />
            </div>
          ))}
        </div>

        {/* Blog Content */}
        <div className="mt-6 text-gray-700 text-lg leading-relaxed px-2" dangerouslySetInnerHTML={{ __html: selectedBlog.content }}></div>

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-10">
          {prev ? (
            <button
              onClick={() => navigate(`/blog?id=${prev}`)}
              className="bg-gray-800 text-white px-5 py-3 rounded-md hover:bg-gray-900 transition-all duration-300"
            >
              ← Previous Blog
            </button>
          ) : <div></div>}

          {next ? (
            <button
              onClick={() => navigate(`/blog?id=${next}`)}
              className="bg-[#f47d02] text-white px-5 py-3 rounded-md hover:bg-orange-600 transition-all duration-300"
            >
              Next Blog →
            </button>
          ) : <div></div>}
        </div>
      </div>
    </div>
  );
}
